import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { roundCrossSection } from "./avatar.js";
import { cheapTextures, worldProjectUV } from "./mapTextures.js";
import { eyeTexture, monsterMaps, type EyeKind, type MonsterSurface } from "./monsterTextures.js";
import { gfxLevel } from "./renderer.js";


/* ---------------------------------------------------------------------------
 * CREATURE BUILD TOOLKIT (2026-08-01, mission `graphics`).
 *
 * Lifted verbatim out of `monsters.ts` on 2026-08-01 (the BEAST pass) so the
 * DRAGON and the SANDWORM in `worldEntities.ts` could be rebuilt on exactly the
 * same primitives as the kaiju instead of a second, divergent copy. `monsters.ts`
 * now imports from here; nothing about the six monsters changed in the move.
 *
 * Every monster used to be `new THREE.BoxGeometry` in a flat colour — a kaiju
 * is the biggest thing this game ever draws and it was also the crudest. Three
 * rules for the rebuild, matching the avatar/weapon/vehicle passes that came
 * before it:
 *
 *  1. Volumes are ROUNDED and ELLIPTICAL-SECTIONED (`vol`), because a torso, a
 *     thigh and a snout are not cubes even at 40 m — the flat-sided silhouette
 *     is most of what read as "Minecraft" here.
 *  2. Every lit surface carries a procedural albedo + normal + roughness from
 *     `monsterTextures` (scale / fur / chitin / ceph / hide / flesh / horn /
 *     burlap), UV'd in WORLD units so the scale rows are the same size on a 6 m
 *     flank as on a 0.9 m forearm.
 *  3. Pieces sharing a material are MERGED into one buffer. A rebuilt godzilla
 *     has ~30 more volumes than the box version but FEWER draw calls, which is
 *     what keeps a three-kaiju map affordable.
 * ------------------------------------------------------------------------- */

export interface Piece {
  g: THREE.BufferGeometry;
  p?: readonly [number, number, number];
  r?: readonly [number, number, number];
  s?: readonly [number, number, number];
}

const _bkMat = new THREE.Matrix4();
const _bkEuler = new THREE.Euler();
const _bkQuat = new THREE.Quaternion();
const _bkPos = new THREE.Vector3();
const _bkScale = new THREE.Vector3();

/** Bake a piece list into ONE geometry. Sources are disposed; the result is
 *  owned by the caller (`dispose` frees it with the monster). */
export function bake(pieces: Piece[]): THREE.BufferGeometry {
  const out: THREE.BufferGeometry[] = [];
  for (const piece of pieces) {
    // mergeGeometries refuses a mix of indexed and non-indexed inputs, and
    // RoundedBoxGeometry is already non-indexed while the primitives are not.
    const g = piece.g.index ? piece.g.toNonIndexed() : piece.g;
    if (g !== piece.g) piece.g.dispose();
    _bkPos.set(piece.p?.[0] ?? 0, piece.p?.[1] ?? 0, piece.p?.[2] ?? 0);
    _bkEuler.set(piece.r?.[0] ?? 0, piece.r?.[1] ?? 0, piece.r?.[2] ?? 0);
    _bkQuat.setFromEuler(_bkEuler);
    _bkScale.set(piece.s?.[0] ?? 1, piece.s?.[1] ?? 1, piece.s?.[2] ?? 1);
    g.applyMatrix4(_bkMat.compose(_bkPos, _bkQuat, _bkScale));
    for (const k of Object.keys(g.attributes)) {
      if (k !== "position" && k !== "normal" && k !== "uv") g.deleteAttribute(k);
    }
    out.push(g);
  }
  const merged = mergeGeometries(out) ?? out[0];
  for (const g of out) if (g !== merged) g.dispose();
  return merged;
}

/** A rounded box bent to an ELLIPTICAL cross-section — the monster analogue of
 *  the avatar's body parts (`round` 0 = box, 1 = full ellipse). */
export function vol(w: number, h: number, d: number, round = 0.8): THREE.BufferGeometry {
  const r = Math.min(w, h, d) * 0.24;
  const g = new RoundedBoxGeometry(w, h, d, 2, r) as unknown as THREE.BufferGeometry;
  roundCrossSection(g, w, d, round);
  return g;
}

/** A small soft-tissue mass (muscle belly, toe, wart, lip): a plain ELLIPSOID
 *  of full size w × h × d. ~140 triangles against `vol`'s 300 — the rich-tier
 *  anatomy adds dozens of these, and at their size the rounded-box corners
 *  `vol` exists for are invisible anyway. The 4th argument is accepted and
 *  ignored so a `vol(...)` call converts in place. */
export function blob(w: number, h: number, d: number, _round = 1, seg = 10): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(0.5, seg, Math.max(4, seg - 2));
  g.scale(w, h, d); // BufferGeometry.scale → applyMatrix4, which fixes the normals
  return g;
}

/** A tapering limb/tail segment: an elliptical volume squeezed at one end. */
export function taperVol(w: number, h: number, d: number, tip: number, round = 0.85): THREE.BufferGeometry {
  const g = vol(w, h, d, round);
  const pos = g.attributes.position, nor = g.attributes.normal;
  const hy = h / 2;
  const kp = (tip - 1) / h;                    // dk/dy, constant
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), z = pos.getZ(i);
    const k = 1 + (tip - 1) * (pos.getY(i) + hy) / h;
    pos.setX(i, x * k);
    pos.setZ(i, z * k);
    // F(x,y,z) = (k(y)x, y, k(y)z)  ⇒  n' = (J⁻¹)ᵀn = (nx/k, ny − k'(x·nx + z·nz)/k, nz/k)
    const nx = nor.getX(i), ny = nor.getY(i), nz = nor.getZ(i);
    const mx = nx / k, mz = nz / k, my = ny - (kp * (x * nx + z * nz)) / k;
    const l = Math.hypot(mx, my, mz) || 1;
    nor.setXYZ(i, mx / l, my / l, mz / l);
  }
  pos.needsUpdate = true; nor.needsUpdate = true;
  return g;
}

/** Scale a geometry's own UVs (for spheres/cones/cylinders, whose seams a
 *  world box-projection would break). */
export function scaleUV(geo: THREE.BufferGeometry, ku: number, kv = ku): void {
  const uv = geo.attributes.uv;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * ku, uv.getY(i) * kv);
  uv.needsUpdate = true;
}

export interface SkinOpts {
  emissive?: string;
  ei?: number;
  rough?: number;
  metal?: number;
  /** `world` = box-projected world-unit UVs (metres per repeat, the default for
   *  rounded volumes); `own` = multiply the primitive's own UVs. */
  uv?: "world" | "own";
  shadow?: boolean;
}

/** One merged, surfaced, elliptical mesh. `tile` is metres-per-repeat for
 *  `world` UVs, repeats-per-primitive for `own`. */
export function surf(
  surface: MonsterSurface, color: string, tile: number, pieces: Piece[], opts: SkinOpts = {},
): THREE.Mesh {
  const geo = bake(pieces);
  if (opts.uv === "own") scaleUV(geo, tile);
  else worldProjectUV(geo, tile);
  const params: THREE.MeshStandardMaterialParameters = {
    color,
    roughness: opts.rough ?? 0.85,
    metalness: opts.metal ?? 0,
    ...(opts.emissive ? { emissive: opts.emissive, emissiveIntensity: opts.ei ?? 0.9 } : {}),
  };
  const m = new THREE.Mesh(geo, new THREE.MeshStandardMaterial(monsterMaps(surface, params)));
  m.castShadow = opts.shadow ?? true;
  return m;
}

/** A claw / tooth / horn: a slightly curved keratin spike. */
export function spike(r: number, len: number): THREE.BufferGeometry {
  const g = new THREE.ConeGeometry(r, len, 6, 2);
  const pos = g.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getY(i) + len / 2) / len;
    pos.setZ(i, pos.getZ(i) - t * t * len * 0.22); // curve the tip forward
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
  return g;
}


/** Extra ANATOMY tier gate (2026-10-23, the "realistic mobs" pass). True only
 *  on a full-quality client (cost ladder at L0 and no low-spec / TV flag) at the
 *  moment the creature is BUILT — monsters are prebuilt under the load screen
 *  (match.ts prebuildMapBeasts), so in practice this is the machine's starting
 *  tier. When false the builders produce the pre-pass part list: same draw
 *  calls, same triangles, so a weak machine pays nothing for this pass. The
 *  only changes that are NOT gated are the ones that make every machine
 *  cheaper (merging same-material parts) or are free (eye pictures). */
export function richCreatures(): boolean {
  return gfxLevel() === 0 && !cheapTextures();
}

/** A PAIR of eyeballs as ONE merged mesh wearing the species' front-on eye
 *  picture (`eyeTexture`). Each ball gets PLANAR uvs along its forward (-z)
 *  axis before it is placed, so the iris lands undistorted on the front and
 *  the sclera border wraps the sides. `glow` > 0 makes the picture its own
 *  emissiveMap (iris glows, pupil stays black). Tagged `bare` — a hide normal
 *  map on a wet eye is worse than none (bench-monster-shape skips these). */
export function eyePair(
  kind: EyeKind, r: number,
  eyes: readonly { p: readonly [number, number, number]; yaw?: number; s?: readonly [number, number, number] }[],
  glow = 0, rough = 0.12,
): THREE.Mesh {
  const rich = richCreatures();
  const pieces: Piece[] = eyes.map((e) => {
    const g = rich ? new THREE.SphereGeometry(r, 20, 14) : new THREE.SphereGeometry(r, 12, 10);
    const pos = g.attributes.position, uv = g.attributes.uv;
    // planar along -z; a mirrored copy lands on the back, which sits inside
    // the skull and is never seen
    for (let i = 0; i < pos.count; i++) uv.setXY(i, 0.5 - pos.getX(i) / (2 * r), 0.5 + pos.getY(i) / (2 * r));
    uv.needsUpdate = true;
    return { g, p: e.p, r: [0, e.yaw ?? 0, 0] as const, s: e.s };
  });
  const tex = eyeTexture(kind);
  const mat = new THREE.MeshStandardMaterial({
    map: tex, roughness: rough, metalness: 0,
    ...(glow > 0 ? { emissive: "#ffffff", emissiveMap: tex, emissiveIntensity: glow } : {}),
  });
  mat.userData.bare = true;
  const m = new THREE.Mesh(bake(pieces), mat);
  m.castShadow = false;
  return m;
}
